/**
 * Self-schedule calendar — multi-slot picker using FullCalendar
 * directly. Shares the toolbar + week grid + busy-event rendering
 * with ManualCalendar; layers the self-schedule-unique UX on top:
 *
 *   - Per-day column checkbox  (toggles duration-sized slot
 *                              auto-generation for that day)
 *   - ALL / week checkbox      (select / deselect all available days)
 *   - Slot pill with × button  (remove a single generated or custom
 *                              slot)
 *   - Drag-to-create custom    (user drags a window → custom slot)
 *
 * The state + slot generation live in `./selfSchedule/`. This file
 * is the FullCalendar glue: wire events, render UI bits, forward
 * callbacks.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import moment from 'moment-timezone';
import type { EventInput } from '@fullcalendar/core';
import { KText } from '@/ui-library/components/text';
import { CalendarToolbar } from './CalendarToolbar';
import { BusyEventContent } from './events';
import type { ManualCalendarExternalEvent } from './hooks';
import {
  BG_EVENT_CLASSES,
  EVENT_PRIORITY,
  EVENT_STYLES,
  busyEventChunkBounds,
  getScrollTime,
  getScrollTimeForNow,
  splitEventAcrossDays,
} from './events';
import {
  AllDaysCheckbox,
  DayCheckboxHeader,
  SlotPill,
} from './selfSchedule/ui';
import { useSelfScheduleSlots, type SelfScheduleHandle } from './selfSchedule';
import type { BusyEvent, Interval } from '../availability';
import './calendar.css';

/**
 * When `true`, drag-to-create and click-to-create refuse to paint a
 * slot whose visible span touches another slot's buffer zone. Flip
 * to `false` if product wants the permissive behavior back (user can
 * overlap buffers manually; only the generator respects them).
 *
 * This constant is a behavior toggle — the feature itself is gated by
 * `feInterviewBuffer`. DetailsStep forces before/after to 0 when the
 * flag is off or the user's eye-toggle is off, so this check has no
 * practical effect unless buffer is actively in use.
 */
const BLOCK_CLICK_IN_BUFFER = false;

/**
 * Build the 7 `YYYY-MM-DD` keys for the calendar week containing
 * `anchor`, expressed in the calendar's timezone. Sun → Sat to match
 * `firstDay={0}` (see project's load-bearing "calendar week is
 * Sun-Sat throughout" rule).
 *
 * Used as the per-render scope for the "ALL" week checkbox — every
 * action against that checkbox stays bound to the days currently
 * visible, not the multi-week pipeline lookahead.
 */
function buildVisibleWeekDateKeys(anchor: moment.Moment): string[] {
  const start = anchor.clone().startOf('week');
  return Array.from({ length: 7 }, (_, i) =>
    start.clone().add(i, 'day').format('YYYY-MM-DD')
  );
}

interface SelfScheduleCalendarProps {
  duration: number;
  timezone?: string;
  onSlotsChange?: (
    _slots: Array<{
      start: string;
      end: string;
      is_custom?: boolean;
      override_type?:
        | 'none'
        | 'calendar'
        | 'working_hours'
        | 'calendar_working_hours'
        | 'buffer'
        | 'buffer_calendar'
        | 'buffer_working_hours'
        | 'wh_cal_buffer';
      _fromServer?: boolean;
    }>
  ) => void;
  /**
   * Edit-mode seed — flat list of already-committed `{start, end}`
   * ISO pairs. Passed straight to `seedFromInitial` which groups by
   * date in the calendar's `timezone`. The reducer's lazy init
   * consumes these on first mount only; later changes to this prop
   * do not re-seed (that would clobber the user's picks).
   *
   * Carries the backend override flags (`is_custom`, `override_type`)
   * so the seeded `SelfSlot` is marked `_fromServer` and the payload
   * mapper preserves the classification verbatim until the user
   * touches the slot.
   */
  initialSlots?: Array<{
    start: string;
    end: string;
    isBooked?: boolean;
    is_custom?: boolean;
    override_type?:
      | 'none'
      | 'calendar'
      | 'working_hours'
      | 'calendar_working_hours'
      | 'buffer'
      | 'buffer_calendar'
      | 'buffer_working_hours'
      | 'wh_cal_buffer';
  }>;
  goToDate?: Date | string;
  /** Read-only busy events (interviewer + meeting-room). */
  externalEvents?: ManualCalendarExternalEvent[];
  /** Background overlays (unavailable hours, minimum notice). */
  overlayEvents?: EventInput[];
  onTimezoneChange?: (_timezone: string) => void;
  onActiveRangeChange?: (_range: { start: string; end: string }) => void;
  /** Common availability across selected interviewers. */
  availability?: Interval[];
  /**
   * Per-interviewer working hours. Required (alongside `interviewKind`)
   * so the pipeline can run per-user busy subtraction for multi-user
   * one_on_one — see `runSlotPipeline` for the rationale.
   */
  scheduleByUserId?: Record<string, Interval[]>;
  /** Busy events reshaped for the slot generator. */
  busyEventsForAlgorithm?: BusyEvent[];
  /** Whether any interviewer is picked — drives empty-state tooltips. */
  hasInterviewer?: boolean;
  /** How many interviewers are picked — pluralizes tooltip copy. */
  interviewerCount?: number;
  /** `one_on_one` vs `panel` — pluralizes tooltip copy for multis. */
  interviewKind?: 'one_on_one' | 'panel';
  /** Title rendered on the booked slot pill in send-additional-slots
   *  flow. Mirrors the manual-mode `BookedEventContent`'s title source. */
  interviewName?: string;
  minimumNoticeHours?: number;
  /** Clearance (minutes) around each generated slot. Default 0/0. */
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * Emitted whenever the set of days that have pipeline availability
   * changes — drives the Find Time walker from the form side
   * (DetailsStep stores the list; `FindTimeField` uses it to compute
   * dynamic date-range labels).
   */
  onAvailableDaysChange?: (_dateKeys: string[]) => void;
}

// ─────────────────────────────────────────────────────────────────
// Tooltip wording — mirrors legacy `SchCalHeader.getDisableReason`.
// Copy differs for multi-interviewer setups so the user knows why
// a specific day's checkbox is off (one person OOO vs. everyone).
// ─────────────────────────────────────────────────────────────────

const DISABLED_NO_INTERVIEWER = 'Pick an interviewer to select slots';
const DISABLED_PAST = 'Cannot select slots in the past';
const DISABLED_NO_AVAILABILITY_SINGLE =
  'Interviewer is not available on this day';
const DISABLED_NO_AVAILABILITY_ALL =
  'All interviewers are not available on this day';
const DISABLED_NO_AVAILABILITY_SOME =
  'Some interviewers are not available on this day';

function noAvailabilityCopy(
  interviewerCount: number,
  interviewKind: 'one_on_one' | 'panel'
): string {
  if (interviewerCount <= 1) return DISABLED_NO_AVAILABILITY_SINGLE;
  return interviewKind === 'one_on_one'
    ? DISABLED_NO_AVAILABILITY_ALL
    : DISABLED_NO_AVAILABILITY_SOME;
}

function gridForDuration(duration: number): string {
  if (duration >= 15) return '00:15:00';
  if (duration >= 10) return '00:10:00';
  return '00:05:00';
}

function gridMsForDuration(duration: number): number {
  if (duration >= 15) return 15 * 60_000;
  if (duration >= 10) return 10 * 60_000;
  return 5 * 60_000;
}

export const SelfScheduleCalendar = forwardRef<
  SelfScheduleHandle,
  SelfScheduleCalendarProps
>(function SelfScheduleCalendar(
  {
    duration,
    timezone: timezoneProp,
    onSlotsChange,
    initialSlots,
    goToDate,
    externalEvents = [],
    overlayEvents = [],
    onTimezoneChange,
    onActiveRangeChange,
    availability = [],
    scheduleByUserId,
    busyEventsForAlgorithm = [],
    hasInterviewer = false,
    interviewerCount = 0,
    interviewKind = 'one_on_one',
    interviewName,
    minimumNoticeHours = 0,
    bufferBeforeMinutes = 0,
    bufferAfterMinutes = 0,
    onAvailableDaysChange,
  },
  ref
) {
  const calendarRef = useRef<FullCalendar>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const timezone = timezoneProp || moment.tz.guess();
  const onDatesSetRef = useRef<(() => void) | null>(null);

  // Track the 7 dateKeys of the currently visible calendar week so the
  // "ALL" checkbox / `toggleAll` operate strictly on what the user can
  // see. Seeded from `goToDate` (or now) before FullCalendar's first
  // `datesSet` fires; updated on every week navigation afterwards.
  const [visibleDateKeys, setVisibleDateKeys] = useState<string[]>(() => {
    const anchor = goToDate
      ? moment.tz(goToDate, timezone)
      : moment.tz(timezone);
    return buildVisibleWeekDateKeys(anchor);
  });

  const {
    activeSlots,
    dayRows,
    weekSelection,
    toggleDay,
    toggleAll,
    removeSlot,
    clickCreate,
    moveSlot,
    applyFindTime,
    clearAll,
  } = useSelfScheduleSlots({
    availability,
    busyEvents: busyEventsForAlgorithm,
    duration,
    timezone,
    minimumNoticeHours,
    bufferBeforeMinutes,
    bufferAfterMinutes,
    hasInterviewer,
    scheduleByUserId,
    interviewKind,
    initialSlots,
    onSlotsChange,
    visibleDateKeys,
  });

  // Find Time triggers a reducer dispatch; the new slots only appear
  // in `activeSlots` on the next render. We can't `scrollToTime` from
  // inside `applyFindTime` directly because the slot times aren't
  // known yet — flip this flag and let the effect below pick up the
  // first slot once the next render lands.
  const pendingFindTimeScrollRef = useRef(false);

  // Imperative handle — DetailsStep drives Find Time + Clear from
  // the form pane without having to lift `useSelfScheduleSlots` up
  // two levels. `applyFindTime` and `clearAll` route straight to the
  // reducer-backed dispatchers.
  useImperativeHandle(
    ref,
    () => ({
      applyFindTime: (args: { targetDateKeys: string[] }) => {
        pendingFindTimeScrollRef.current = true;
        applyFindTime(args);
      },
      clearAll: () => clearAll(),
      goToDate: (date: string | Date) => {
        // Use the underlying FullCalendar API directly. When the
        // date is a `YYYY-MM-DD` string, `gotoDate` accepts it and
        // scrolls to the containing week.
        const parsed = typeof date === 'string' ? new Date(date) : date;
        calendarRef.current?.getApi()?.gotoDate(parsed);
      },
    }),
    [applyFindTime, clearAll]
  );

  // After Find Time apply, scroll the calendar's time axis to the
  // first generated slot so the user sees the picks immediately
  // instead of staring at whatever hour was last scrolled to.
  // `applyFindTime` flips the flag; this effect consumes it once
  // `activeSlots` reflects the new picks.
  useEffect(() => {
    if (!pendingFindTimeScrollRef.current) return;
    // Always consume the flag, even when there are no slots — leaving
    // it set would scroll on the next unrelated activeSlots change.
    pendingFindTimeScrollRef.current = false;
    const first = activeSlots[0];
    if (!first?.start) return;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    api.scrollToTime(getScrollTime(moment.tz(first.start, timezone).hour()));
  }, [activeSlots, timezone]);

  // Mirror duration into a ref so the `eventContent` useCallback can
  // read it without taking it as a dep — adding `duration` to the
  // callback's dep list re-mints the callback on every NumberStepper
  // tick, and FullCalendar then re-renders all event harnesses,
  // causing visible jank in dense calendars.
  const durationRef = useRef(duration);
  durationRef.current = duration;

  // In edit mode `initialSlots` seeds the reducer so `activeSlots` is
  // populated on the very first render. Navigate to the first slot's
  // week and scroll to its time position once — the `initialDate` useMemo
  // below handles the week correctly when activeSlots is already available,
  // but scrollTime always defaults to "now". The imperative call is the
  // only reliable way to scroll to the slot's time on mount.
  const didNavigateRef = useRef(false);
  useEffect(() => {
    if (didNavigateRef.current || !initialSlots?.length) return;
    const first = activeSlots[0];
    if (!first?.start) return;
    const api = calendarRef.current?.getApi();
    if (!api) return;
    didNavigateRef.current = true;
    api.gotoDate(new Date(first.start));
    api.scrollToTime(getScrollTime(moment.tz(first.start, timezone).hour()));
  }, [initialSlots, activeSlots, timezone]);

  // Emit the set of days with pipeline availability whenever it
  // changes — the form-side `FindTimeField` needs this to compute
  // dynamic date-range labels and to walk N weekdays forward.
  useEffect(() => {
    if (!onAvailableDaysChange) return;
    const keys = Object.entries(dayRows)
      .filter(([, row]) => row.hasAvailability)
      .map(([dateKey]) => dateKey);
    onAvailableDaysChange(keys);
  }, [dayRows, onAvailableDaysChange]);

  const handleTimezoneChange = useCallback(
    (tz: string) => onTimezoneChange?.(tz),
    [onTimezoneChange]
  );

  // ─── Event feeds ──────────────────────────────────────────────
  // Foreground events so overlapping blocks split side-by-side.
  // Clicks handled by the inline onClick on BusyEventContent.
  //
  // Multi-day events (e.g. 24h OOO crossing midnight) are split into
  // per-day chunks so each visible segment carries its own start/end
  // time — without this, FC's per-segment label collapses to
  // "9:30 PM – 9:30 PM" because the global event bounds are identical
  // hour-minute strings. Matches legacy `schProcessCalendarEvents.ts`.
  const busyEvents = useMemo<EventInput[]>(
    () =>
      externalEvents
        .flatMap((evt) => splitEventAcrossDays(evt, timezone))
        .map((evt, i) => {
          // Free events: dashed-border ghost block rendered as a
          // background event so picks can sit on top. Mirrors
          // `useManualCalendarEvents` — keep both calendars in sync.
          const isFree = evt.status === 'free';
          return {
            id: evt.id ? `${evt.id}-${i}` : `ext-${evt.start}-${evt.end}`,
            start: evt.start,
            end: evt.end,
            title: evt.title ?? 'Busy',
            display: isFree ? ('background' as const) : undefined,
            backgroundColor:
              evt.backgroundColor ?? EVENT_STYLES.busy.backgroundColor,
            borderColor: isFree
              ? (evt.textColor ?? EVENT_STYLES.busy.borderColor)
              : (evt.borderColor ?? EVENT_STYLES.busy.borderColor),
            textColor: evt.textColor ?? EVENT_STYLES.busy.textColor,
            classNames:
              evt.classNames ??
              (isFree ? 'im-interviewer-free' : 'im-interviewer-busy'),
            editable: false,
            durationEditable: false,
            startEditable: false,
            extendedProps: {
              category: 'busy',
              priority: EVENT_PRIORITY.busy,
              tooltipTitle: evt.tooltipTitle ?? evt.title,
              status: evt.status,
            },
          };
        }),
    [externalEvents, timezone]
  );

  const pickedEvents = useMemo<EventInput[]>(
    () =>
      activeSlots.map((s) => ({
        id: s.id,
        start: s.start,
        end: s.end,
        title: 'Slot',
        // Booked slot (candidate already picked it — shown during the
        // "Send additional slots" flow) reuses the manual-mode booked
        // event style (`EVENT_STYLES.booked`): solid primary fill so
        // recruiters recognise the confirmed slot at a glance, locked
        // against drag / resize.
        backgroundColor: s.isBooked
          ? EVENT_STYLES.booked.backgroundColor
          : 'var(--kula-colors-primary-50)',
        borderColor: s.isBooked
          ? EVENT_STYLES.booked.borderColor
          : 'var(--kula-colors-primary-300)',
        textColor: s.isBooked
          ? EVENT_STYLES.booked.textColor
          : 'var(--kula-colors-primary-700)',
        editable: !s.isBooked,
        durationEditable: false,
        startEditable: !s.isBooked,
        classNames: s.isBooked
          ? 'im-self-slot im-self-slot--booked'
          : 'im-self-slot',
        extendedProps: {
          category: 'picked',
          priority: EVENT_PRIORITY.available,
          origin: s.origin,
          isBooked: Boolean(s.isBooked),
          // Title shown on the booked pill (manual-mode parity).
          interviewName,
          // `override_type` is set on edit-hydrated slots
          // (`seedFromInitial` carries the backend value through). The
          // `SlotPill` consumes it to render the "Custom slot"
          // tooltip. Live user picks don't have it yet because we
          // compute lazily at save time — see the note on `SlotPill`.
          overrideType: s.override_type,
        },
      })),
    [activeSlots, interviewName]
  );

  const pastOverlay = useMemo<EventInput>(
    () => ({
      start: moment()
        .tz(timezone)
        .subtract(10, 'year')
        .format('YYYY-MM-DDTHH:mm:ssZ'),
      end: moment().tz(timezone).format('YYYY-MM-DDTHH:mm:ssZ'),
      display: 'background',
      classNames: BG_EVENT_CLASSES.disabledSlots,
      color: 'transparent',
    }),
    [timezone]
  );

  // Diagonal-stripe overlays framing each slot with its before/after
  // clearance. Rendered as background events so they sit behind the
  // slot pill and don't intercept clicks. Emitted only when buffer is
  // active — for zero values the array is empty and there's no extra
  // layer to render.
  const bufferEvents = useMemo<EventInput[]>(() => {
    if (bufferBeforeMinutes <= 0 && bufferAfterMinutes <= 0) return [];
    const out: EventInput[] = [];
    for (const s of activeSlots) {
      const startMs = moment.tz(s.start, timezone).valueOf();
      const endMs = moment.tz(s.end, timezone).valueOf();
      if (bufferBeforeMinutes > 0) {
        out.push({
          id: `buffer-before-${s.id}`,
          start: moment
            .tz(startMs - bufferBeforeMinutes * 60_000, timezone)
            .toISOString(),
          end: s.start,
          display: 'background',
          classNames: BG_EVENT_CLASSES.slotBuffer,
          color: 'transparent',
        });
      }
      if (bufferAfterMinutes > 0) {
        out.push({
          id: `buffer-after-${s.id}`,
          start: s.end,
          end: moment
            .tz(endMs + bufferAfterMinutes * 60_000, timezone)
            .toISOString(),
          display: 'background',
          classNames: BG_EVENT_CLASSES.slotBuffer,
          color: 'transparent',
        });
      }
    }
    return out;
  }, [activeSlots, bufferBeforeMinutes, bufferAfterMinutes, timezone]);

  const events = useMemo(
    () => [
      ...overlayEvents,
      ...busyEvents,
      ...bufferEvents,
      ...pickedEvents,
      pastOverlay,
    ],
    [overlayEvents, busyEvents, bufferEvents, pickedEvents, pastOverlay]
  );

  // ─── Helpers ─────────────────────────────────────────────────

  /** Build a duration-wide window from any `startIso`. */
  const durationWindow = useCallback(
    (startIso: string) => {
      const dateKey = moment.tz(startIso, timezone).format('YYYY-MM-DD');
      const startMs = moment.tz(startIso, timezone).valueOf();
      const startNorm = moment.tz(startMs, timezone).toISOString();
      const endNorm = moment
        .tz(startMs + duration * 60_000, timezone)
        .toISOString();
      return { dateKey, startIso: startNorm, endIso: endNorm };
    },
    [duration, timezone]
  );

  // ─── User intents wired to the reducer-backed hook ──────────
  // Legacy parity: a custom slot can partially overlap an existing
  // slot (10:15-10:45 on top of a 10:30-11:00 generated one). The
  // only case we block is an **exact duplicate** — same dateKey,
  // start, and end as an existing slot. The `clickCreate` reducer's
  // `fullyCoveredBy` guard handles that silently (no toast, the
  // click is simply a no-op). Past-time blocks still apply.

  // Earliest ms epoch the user can place a pick at — `now + notice`.
  // Every create path guards against this. Recomputed per click so
  // long-lived calendars stay accurate without a setInterval.
  const earliestAllowedMs = useCallback(
    () => moment().tz(timezone).valueOf() + minimumNoticeHours * 3_600_000,
    [minimumNoticeHours, timezone]
  );

  // Returns `true` when a proposed `[startMs, endMs)` overlaps any
  // existing slot's buffer zone (slot minus before, slot plus after)
  // or the slot itself. Caller decides what to do with it — under
  // `BLOCK_CLICK_IN_BUFFER` we use it to reject clicks/drags.
  const overlapsAnyBuffer = useCallback(
    (startMs: number, endMs: number) => {
      if (bufferBeforeMinutes <= 0 && bufferAfterMinutes <= 0) return false;
      const beforeMs = bufferBeforeMinutes * 60_000;
      const afterMs = bufferAfterMinutes * 60_000;
      for (const s of activeSlots) {
        const sStart = moment.tz(s.start, timezone).valueOf();
        const sEnd = moment.tz(s.end, timezone).valueOf();
        const zoneStart = sStart - beforeMs;
        const zoneEnd = sEnd + afterMs;
        if (startMs < zoneEnd && endMs > zoneStart) return true;
      }
      return false;
    },
    [activeSlots, bufferBeforeMinutes, bufferAfterMinutes, timezone]
  );

  /** Drag-release — snaps to a duration-wide slot at the drag start. */
  const handleSelect = useCallback(
    (info: { startStr: string }) => {
      const { dateKey, startIso, endIso } = durationWindow(info.startStr);
      const startMs = moment.tz(startIso, timezone).valueOf();
      const endMs = moment.tz(endIso, timezone).valueOf();
      if (startMs < earliestAllowedMs()) {
        calendarRef.current?.getApi()?.unselect();
        return;
      }
      if (BLOCK_CLICK_IN_BUFFER && overlapsAnyBuffer(startMs, endMs)) {
        calendarRef.current?.getApi()?.unselect();
        return;
      }
      clickCreate({ dateKey, startIso, endIso });
      calendarRef.current?.getApi()?.unselect();
    },
    [
      clickCreate,
      durationWindow,
      earliestAllowedMs,
      overlapsAnyBuffer,
      timezone,
    ]
  );

  /** Single-click — also creates a duration-wide slot. */
  const handleDateClick = useCallback(
    (arg: { dateStr: string }) => {
      const { dateKey, startIso, endIso } = durationWindow(arg.dateStr);
      const startMs = moment.tz(startIso, timezone).valueOf();
      const endMs = moment.tz(endIso, timezone).valueOf();
      if (startMs < earliestAllowedMs()) {
        return;
      }
      if (BLOCK_CLICK_IN_BUFFER && overlapsAnyBuffer(startMs, endMs)) {
        return;
      }
      clickCreate({ dateKey, startIso, endIso });
    },
    [
      clickCreate,
      durationWindow,
      earliestAllowedMs,
      overlapsAnyBuffer,
      timezone,
    ]
  );

  // ─── Drag to move / resize a picked slot ─────────────────────
  // Drag-move rules (per product):
  //   - Any vertical/horizontal move is allowed (including onto a
  //     cell that partially overlaps another slot).
  //   - Revert ONLY when the new position is an **exact duplicate**
  //     of another slot — same `(start, end)` tuple. Otherwise the
  //     user would be creating two visually-identical picks.
  //   - Custom slots keep their id (in-place move), so FullCalendar
  //     doesn't unmount+remount the event — no more "revert flash".
  //   - Generated slots drag → converted to custom with a fresh id;
  //     the old generated id lands in `droppedIds` so the pipeline
  //     doesn't re-emit it.
  const handleEventChange = useCallback(
    (arg: {
      event: {
        id: string;
        startStr: string;
        endStr: string;
        extendedProps: Record<string, unknown>;
      };
      revert: () => void;
    }) => {
      if (arg.event.extendedProps?.category !== 'picked') {
        arg.revert();
        return;
      }
      const newDateKey = moment
        .tz(arg.event.startStr, timezone)
        .format('YYYY-MM-DD');
      const wasGenerated = arg.event.extendedProps?.origin === 'generated';
      const newStartMs = Date.parse(arg.event.startStr);
      const newEndMs = Date.parse(arg.event.endStr);
      // Minimum-notice: can't drop a slot into the notice window.
      if (newStartMs < earliestAllowedMs()) {
        arg.revert();
        return;
      }

      // Exact-duplicate check: same dateKey AND same start AND same
      // end as another slot. Partial overlap is fine — the user
      // asked for free-form drag.
      const duplicate = activeSlots.find((s) => {
        if (s.id === arg.event.id) return false;
        if (s.dateKey !== newDateKey) return false;
        const sStart = Date.parse(s.start);
        const sEnd = Date.parse(s.end);
        return sStart === newStartMs && sEnd === newEndMs;
      });
      if (duplicate) {
        arg.revert();
        return;
      }
      // `flushSync` commits the state update synchronously inside
      // FullCalendar's eventDrop callback. Without it, React's
      // async batching means FC gets its next render after a brief
      // tick during which the `events` prop still holds the old
      // position — FC then snaps the event back to the old spot
      // before accepting the new state, which reads as a flicker.
      flushSync(() => {
        moveSlot({
          slotId: arg.event.id,
          wasGenerated,
          newDateKey,
          newStartIso: arg.event.startStr,
          newEndIso: arg.event.endStr,
        });
      });
    },
    [activeSlots, earliestAllowedMs, moveSlot, timezone]
  );

  // ─── Initial focus date ──────────────────────────────────────
  const initialDate = useMemo(() => {
    if (goToDate) {
      return typeof goToDate === 'string' ? new Date(goToDate) : goToDate;
    }
    const first = activeSlots[0]?.start;
    return first ? new Date(first) : undefined;
  }, [goToDate, activeSlots]);

  // ─── Event rendering ─────────────────────────────────────────
  const renderEventContent = useCallback(
    (eventInfo: {
      event: {
        id: string;
        title: string;
        startStr: string;
        endStr: string;
        extendedProps: Record<string, unknown>;
      };
    }) => {
      const category = eventInfo.event.extendedProps?.category as string;
      if (category === 'busy') {
        const startMs = new Date(eventInfo.event.startStr).getTime();
        const endMs = new Date(eventInfo.event.endStr).getTime();
        // Past events drop the click handler (see ManualCalendar
        // comment — same reasoning) so the cursor stays default
        // instead of misleadingly suggesting clickability.
        const isPastEvent = endMs <= Date.now();
        return (
          <BusyEventContent
            title={eventInfo.event.title}
            tooltipTitle={
              eventInfo.event.extendedProps?.tooltipTitle as string | undefined
            }
            isFree={eventInfo.event.extendedProps?.status === 'free'}
            start={eventInfo.event.startStr}
            end={eventInfo.event.endStr}
            timezone={timezone}
            onSlotClick={
              isPastEvent
                ? undefined
                : (e) => {
                    const { chunkStartMs, chunkEndMs } = busyEventChunkBounds(
                      e.currentTarget,
                      startMs,
                      endMs,
                      timezone
                    );
                    const rect = e.currentTarget.getBoundingClientRect();
                    const relY = Math.max(
                      0,
                      Math.min(rect.height, e.clientY - rect.top)
                    );
                    const proportion = rect.height > 0 ? relY / rect.height : 0;
                    const clickMs =
                      chunkStartMs + proportion * (chunkEndMs - chunkStartMs);
                    // Snap to the same grid the calendar renders so a click on a
                    // busy-event lands on a visible grid line for short
                    // durations. Without this match, a duration=6 wizard would
                    // render a 5-min grid but the click handler would round the
                    // resulting slot to a 15-min boundary — visually off-grid.
                    const gridMs = gridMsForDuration(durationRef.current);
                    const snappedMs = Math.round(clickMs / gridMs) * gridMs;
                    const snappedIso = moment
                      .tz(snappedMs, timezone)
                      .toISOString();
                    const { dateKey, startIso, endIso } =
                      durationWindow(snappedIso);
                    if (
                      moment.tz(startIso, timezone).valueOf() <
                      earliestAllowedMs()
                    )
                      return;
                    clickCreate({ dateKey, startIso, endIso });
                    calendarRef.current?.getApi()?.unselect();
                  }
            }
          />
        );
      }
      if (category === 'picked') {
        const isGenerated =
          eventInfo.event.extendedProps?.origin === 'generated';
        const isBooked = Boolean(eventInfo.event.extendedProps?.isBooked);
        const overrideType = eventInfo.event.extendedProps?.overrideType as
          | 'none'
          | 'calendar'
          | 'working_hours'
          | 'calendar_working_hours'
          | 'buffer'
          | 'buffer_calendar'
          | 'buffer_working_hours'
          | 'wh_cal_buffer'
          | undefined;
        const pillInterviewName = eventInfo.event.extendedProps
          ?.interviewName as string | undefined;
        return (
          <SlotPill
            slotId={eventInfo.event.id}
            startIso={eventInfo.event.startStr}
            endIso={eventInfo.event.endStr}
            timezone={timezone}
            isGenerated={isGenerated}
            isBooked={isBooked}
            overrideType={overrideType}
            interviewName={pillInterviewName}
            onRemove={removeSlot}
          />
        );
      }
      return (
        <Box p="2px 4px" fontSize="11px" overflow="hidden">
          {eventInfo.event.title}
        </Box>
      );
    },
    [timezone, removeSlot, durationWindow, earliestAllowedMs, clickCreate]
  );

  // Drag-select highlight grows while the drag is shorter-or-equal
  // to `duration`. Longer drags stop extending (FullCalendar holds
  // the last valid width). On release we commit exactly `duration`
  // from the drag start, so the preview can *underestimate* but
  // never *overestimate* the final slot. Past time is blocked.
  //
  // Partial overlap with existing slots is explicitly allowed —
  // the user can drag a 10:15-10:45 slot on top of a 10:30-11:00
  // generated slot; both coexist. Exact-duplicate creation is
  // swallowed by the `clickCreate` reducer's `fullyCoveredBy`
  // guard, so no UX-level check is needed here.
  const selectAllow = useCallback(
    (info: { start: Date; startStr: string; endStr: string }) => {
      // Past + minimum-notice window: drag highlight refuses to
      // paint over either. User sees the dashed preview pause at
      // the notice cutoff and can't commit inside it.
      if (info.start.getTime() < earliestAllowedMs()) return false;
      const dur = moment
        .tz(info.endStr, timezone)
        .diff(moment.tz(info.startStr, timezone), 'minutes');
      if (dur > duration) return false;
      if (BLOCK_CLICK_IN_BUFFER) {
        const startMs = moment.tz(info.startStr, timezone).valueOf();
        const endMs = moment.tz(info.endStr, timezone).valueOf();
        if (overlapsAnyBuffer(startMs, endMs)) return false;
      }
      return true;
    },
    [duration, earliestAllowedMs, overlapsAnyBuffer, timezone]
  );

  const eventAllow = useCallback(
    (info: { start: Date }) => info.start.getTime() >= earliestAllowedMs(),
    [earliestAllowedMs]
  );

  // ─── Day-header checkbox ─────────────────────────────────────
  // The hook already returns `meta.checkbox` as the four-state
  // enum. This renderer just picks the tooltip wording for the
  // `disabled` state (the enum itself can't carry the reason).
  const renderDayHeader = useCallback(
    (args: { date: Date }) => {
      // Decode in the calendar's tz, not the browser's. FC's fake-UTC
      // Date matches the calendar wall-clock — reading it through
      // browser-local moment() shifts the date when tz differs (e.g.
      // browser PST + calendar IST: Friday → Thursday key → toggleDay
      // fires on Thursday while Friday's UI shows checked).
      const dateKey = moment.tz(args.date, timezone).format('YYYY-MM-DD');
      const meta = dayRows[dateKey];
      const isPast = moment
        .tz(args.date, timezone)
        .isBefore(moment().tz(timezone), 'day');

      // Priority order: no-interviewer > past > no-availability.
      // Availability copy depends on interviewer count + kind.
      let reason: string | undefined;
      if (!hasInterviewer) reason = DISABLED_NO_INTERVIEWER;
      else if (isPast) reason = DISABLED_PAST;
      else if (!meta?.hasAvailability)
        reason = noAvailabilityCopy(interviewerCount, interviewKind);

      // The hook doesn't know about past-day or no-interviewer
      // since those are the calendar's concern, so we coerce to
      // `disabled` here when needed. Once the checkbox is disabled
      // the user can't flip it anyway, so rendering `disabled` is
      // safe even if the underlying state was `empty`/`partial`.
      const state =
        !hasInterviewer || isPast || !meta?.hasAvailability
          ? ('disabled' as const)
          : (meta?.checkbox ?? 'empty');

      return (
        <DayCheckboxHeader
          date={args.date}
          state={state}
          disabledReason={reason}
          onToggle={(checked) => toggleDay(dateKey, checked)}
        />
      );
    },
    [
      dayRows,
      hasInterviewer,
      interviewerCount,
      interviewKind,
      toggleDay,
      timezone,
    ]
  );

  // ─── Week-selection "ALL" tooltip reason ────────────────────
  // `weekSelection` is the week-level rollup of the per-day enum.
  // When it's `disabled`, pick the copy based on why the hook said
  // disabled (no interviewer vs. no availability this week). Uses
  // the same pluralised helper as per-day headers for consistent
  // wording.
  const weekSelectionReason =
    weekSelection !== 'disabled'
      ? undefined
      : !hasInterviewer
        ? DISABLED_NO_INTERVIEWER
        : noAvailabilityCopy(interviewerCount, interviewKind);

  return (
    <Flex flexDir="column" h="100%" w="100%" className="im-calendar">
      <CalendarToolbar
        calendarRef={calendarRef}
        timezone={timezone}
        onTimezoneChange={handleTimezoneChange}
        disablePastNavigation
        onDatesSetRef={onDatesSetRef}
      />
      <Box
        ref={hostRef}
        flex="1"
        minH={0}
        position="relative"
        overflow="visible"
        className="im-calendar-fc-scroll-host im-self-schedule"
      >
        <AllDaysCheckbox
          hostElement={hostRef.current}
          state={weekSelection}
          disabledReason={weekSelectionReason}
          onToggle={toggleAll}
        />
        <FullCalendar
          key={timezone}
          ref={calendarRef}
          plugins={[interactionPlugin, timeGridPlugin, momentTimezonePlugin]}
          nowIndicator
          initialView="timeGridWeek"
          initialDate={initialDate}
          dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
          dayHeaderContent={renderDayHeader}
          headerToolbar={false}
          selectable={hasInterviewer}
          // `selectMirror={false}` — we rely on `.fc-highlight`
          // alone so the drag preview doesn't paint two overlapping
          // layers (highlight underneath, mirror on top). Matches
          // ManualCalendar. See `calendar.css` for the highlight
          // treatment.
          selectMirror={false}
          dayMaxEvents
          weekends
          height="100%"
          allDaySlot={false}
          slotDuration={gridForDuration(duration)}
          snapDuration={gridForDuration(duration)}
          slotLabelInterval={{ hours: 1 }}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          expandRows
          timeZone={timezone}
          firstDay={0}
          stickyHeaderDates
          slotEventOverlap
          scrollTime={getScrollTimeForNow(timezone)}
          eventOrderStrict
          eventOrder="-extendedProps.priority,start_time"
          events={events}
          datesSet={(arg) => {
            onDatesSetRef.current?.();
            if (arg?.start) {
              const nextKeys = buildVisibleWeekDateKeys(
                moment.tz(arg.start, timezone)
              );
              setVisibleDateKeys((prev) =>
                prev.length === nextKeys.length &&
                prev.every((k, i) => k === nextKeys[i])
                  ? prev
                  : nextKeys
              );
            }
            if (onActiveRangeChange && arg?.start && arg?.end) {
              onActiveRangeChange({
                start: moment.tz(arg.start, timezone).toISOString(),
                end: moment.tz(arg.end, timezone).toISOString(),
              });
            }
          }}
          select={handleSelect}
          dateClick={handleDateClick}
          eventDrop={handleEventChange}
          eventResize={handleEventChange}
          eventContent={renderEventContent}
          eventDidMount={(info) => {
            // FullCalendar's BgEvent renderer only forwards
            // `backgroundColor` to the DOM, so `borderColor` /
            // `textColor` from the event input are dropped for
            // `display: 'background'` events. Inject them inline here
            // so the dashed border + label colour survive on free
            // events. Mirrors `ManualCalendar` — keep both in sync.
            if (info.event.extendedProps?.status !== 'free') return;
            const { borderColor, textColor } = info.event;
            if (borderColor) {
              info.el.style.borderStyle = 'dashed';
              info.el.style.borderWidth = '1px';
              info.el.style.borderColor = borderColor;
            }
            if (textColor) {
              info.el.style.color = textColor;
            }
          }}
          selectAllow={selectAllow}
          eventAllow={eventAllow}
          slotLabelContent={({ text }) => (
            <KText variant="small_text" w="47px" alignItems="center">
              {text}
            </KText>
          )}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            omitZeroMinute: true,
            meridiem: true,
            separator: ' - ',
          }}
        />
      </Box>
    </Flex>
  );
});

SelfScheduleCalendar.displayName = 'SelfScheduleCalendar';
